import React, { CSSProperties, useState, useEffect } from 'react';
import radioEmpty from '../../assets/radio-button-on.svg';
import radioFilled from '../../assets/radio-button-on-fill.svg';

import {
  DataPoint,
  boysLengthData,
  girlsLengthData,
  boysHeadData,
  girlsHeadData,
  boysWeightData,
  girlsWeightData,
  boysLengthDataWHO,
  girlsLengthDataWHO,
  boysHeadDataWHO,
  girlsHeadDataWHO,
  boysWeightDataWHO,
  girlsWeightDataWHO,
} from './CorrectedAgeManager/dataPoints2025';

import { cumulativeStdNormalProbability, probit } from 'simple-statistics';
import { differenceInMonths, differenceInWeeks } from 'date-fns';
import './CorrectedAge.scss';

import { MeasurementForm } from './CorrectedAgeManager/MeasurementForm';
import { MeasurementGraph } from './CorrectedAgeManager/MeasurementGraph';
import { MEASUREMENTS_CONFIG } from './CorrectedAgeManager/measurementConfig';
import { GraphType, Gender, MeasurementType, MeasurementState } from './CorrectedAgeManager/types';
import { AdditionalMeasurements } from './AdditionalMeasurements';
import { getGraphType } from './CorrectedAgeManager/correctedAgeUtils';

interface GrowthChartsProps {
  gender: 'boy' | 'girl' | undefined;
  gestationWeeks: string | '';
  fixedAgeInWeeks: number | '';
  fentonLength: string;
  fentonHC: string;
  fentonWeight: string;
  setGender: (gender: 'boy' | 'girl') => void;
  setFentonLength: (length: string) => void;
  setFentonHC: (hc: string) => void;
  setFentonWeight: (weight: string) => void;
  birthDate: Date | null;
}

const GrowthCharts: React.FC<GrowthChartsProps> = ({
  gender,
  gestationWeeks,
  fixedAgeInWeeks,
  fentonLength,
  fentonHC,
  fentonWeight,
  setGender,
  setFentonLength,
  setFentonHC,
  setFentonWeight,
  birthDate,
}) => {
  const [activeTab, setActiveTab] = useState<'length' | 'head' | 'weight'>('length');
  const [showLengthGraph, setShowLengthGraph] = useState(!!gender);
  const [showHeadGraph, setShowHeadGraph] = useState(!!gender);
  const [showWeightGraph, setShowWeightGraph] = useState(!!gender);

  const [dataSetLength, setDataSetLength] = useState<DataPoint[]>([]);
  const [dataSetHead, setDataSetHead] = useState<DataPoint[]>([]);
  const [dataSetWeight, setDataSetWeight] = useState<DataPoint[]>([]);

  const [percentageLength, setPercentageLength] = useState('');
  const [percentageHead, setPercentageHead] = useState('');
  const [percentageWeight, setPercentageWeight] = useState('');

  const [lengthMeasurements, setLengthMeasurements] = useState<MeasurementState>({
    birthValue: '',
    additionalEntries: [],
  });
  const [headMeasurements, setHeadMeasurements] = useState<MeasurementState>({
    birthValue: '',
    additionalEntries: [],
  });
  const [weightMeasurements, setWeightMeasurements] = useState<MeasurementState>({
    birthValue: '',
    additionalEntries: [],
  });

  // Normalize fixedAgeInWeeks to a number to avoid NaN propagation
  const fixedWeeksNum = typeof fixedAgeInWeeks === 'number' ? fixedAgeInWeeks : 0;

  // Determine graph type based on conditions
  const graphType = getGraphType(fixedAgeInWeeks, gestationWeeks);

  // Compute WHO months shown on the X-axis
  const getMonth = (): number => {
    // For preterm babies, (PMA - 40) / 4 is a proxy for corrected months.
    // If it goes negative, fall back to chronological months since birth (if birthDate exists).
    let calculatedMonths = Math.round((fixedWeeksNum - 40) / 4);

    if (calculatedMonths < 0 && birthDate) {
      calculatedMonths = differenceInMonths(new Date(), birthDate);
    }

    return calculatedMonths;
  };

  const whoMonths = getMonth();

  // IMPORTANT:
  // WHO datasets use X in months (0..24), Fenton uses X in weeks.
  // We must pass the correct X value everywhere (percentiles + dots + additional measurements).
  const ageForGraph = graphType === 'WHO' ? whoMonths : fixedWeeksNum;

  // Show red border on gender block when user enters a value but gender is not selected
  const showGenderError =
    !gender &&
    ((activeTab === 'length' && !!fentonLength) ||
      (activeTab === 'weight' && !!fentonWeight) ||
      (activeTab === 'head' && !!fentonHC));

  const findPercentage = (
    graphType: GraphType, // 'פנטון' | 'WHO'
    gender: Gender, // 'boy' | 'girl'
    age: number,
    measurement: number,
    measurementType: MeasurementType,
    setPercentage: (value: string) => void
  ): void => {
    if (!gender) {
      setPercentage('יש לבחור מגדר');
      return;
    }

    let data: DataPoint[];

    if (graphType === 'WHO') {
      if (measurementType === 'head') data = gender === 'boy' ? boysHeadDataWHO : girlsHeadDataWHO;
      else if (measurementType === 'length') data = gender === 'boy' ? boysLengthDataWHO : girlsLengthDataWHO;
      else data = gender === 'boy' ? boysWeightDataWHO : girlsWeightDataWHO;
    } else {
      if (measurementType === 'head') data = gender === 'boy' ? boysHeadData : girlsHeadData;
      else if (measurementType === 'length') data = gender === 'boy' ? boysLengthData : girlsLengthData;
      else data = gender === 'boy' ? boysWeightData : girlsWeightData;
    }

    const entry = data.find((item) => item.x === age);

    if (!entry) {
      setPercentage('גיל מחוץ לטווח או לא מוגדר');
      return;
    }

    if (entry.y3 === undefined || entry.y2 === undefined) return;

    // Calculate Z-score
    const calculateZ = (x: number, mean: number, stdDev: number, l?: number, s?: number): number => {
      if (l === undefined || s === undefined) return (x - mean) / stdDev;
      return (Math.pow(x / mean, l) - 1) / (l * s);
    };

    const probitPercentage = graphType === 'WHO' ? 0.25 : 0.1;
    const mean = graphType === 'WHO' ? (entry.m ?? entry.y3) : entry.y3;
    const S = graphType === 'WHO' ? entry.s : undefined;
    const standardDeviation = (entry.y2 - mean) / probit(probitPercentage);
    const L = graphType === 'WHO' ? entry.l : undefined;

    const z = calculateZ(measurement, mean, standardDeviation, L, S);
    const percent = cumulativeStdNormalProbability(z);
    let percentToShow: any = Math.round(percent * 100);

    if (graphType === 'WHO') {
      // Fix rounding for 97.7 / 2.3 boundaries
      if (percentToShow === 98) percentToShow = 97.7;
      else if (percentToShow === 2) percentToShow = 2.3;

      if (percent >= 0.977) setPercentage('מעל אחוזון 97.7');
      else if (percent <= 0.023) setPercentage('מתחת אחוזון 2.3');
      else setPercentage(`אחוזון ${percentToShow}`);
    } else {
      if (percent >= 0.97) setPercentage('מעל אחוזון 97');
      else if (percent <= 0.03) setPercentage('מתחת אחוזון 3');
      else setPercentage(`אחוזון ${percentToShow}`);
    }
  };

  // Convert date -> X coordinate used in the graph for additional measurements
  const calculateGestationWeek = (date: Date): number => {
    const currentDate = new Date();

    if (graphType === 'פנטון') {
      // X is gestational weeks; older dates have smaller X
      return fixedWeeksNum - differenceInWeeks(currentDate, date);
    }

    // WHO: X is months
    return whoMonths - differenceInMonths(currentDate, date);
  };

  useEffect(() => {
    if (gender) {
      // Update datasets based on graph type and gender
      if (graphType === 'WHO') {
        setDataSetLength(gender === 'boy' ? boysLengthDataWHO : girlsLengthDataWHO);
        setDataSetHead(gender === 'boy' ? boysHeadDataWHO : girlsHeadDataWHO);
        setDataSetWeight(gender === 'boy' ? boysWeightDataWHO : girlsWeightDataWHO);
      } else {
        setDataSetLength(gender === 'boy' ? boysLengthData : girlsLengthData);
        setDataSetHead(gender === 'boy' ? boysHeadData : girlsHeadData);
        setDataSetWeight(gender === 'boy' ? boysWeightData : girlsWeightData);
      }

      // Show graphs when gender is selected
      setShowLengthGraph(true);
      setShowHeadGraph(true);
      setShowWeightGraph(true);

      // Calculate percentages for entered values
      if (!isNaN(parseFloat(fentonHC))) {
        findPercentage(graphType, gender, ageForGraph, parseFloat(fentonHC), 'head', setPercentageHead);
      }
      if (!isNaN(parseFloat(fentonLength))) {
        findPercentage(graphType, gender, ageForGraph, parseFloat(fentonLength), 'length', setPercentageLength);
      }
      if (!isNaN(parseFloat(fentonWeight))) {
        findPercentage(graphType, gender, ageForGraph, parseFloat(fentonWeight), 'weight', setPercentageWeight);
      }
    } else {
      // Hide graphs when no gender selected
      setShowWeightGraph(false);
      setShowLengthGraph(false);
      setShowHeadGraph(false);
    }

    // Reset measurements when graph type or gender changes
    setLengthMeasurements({ birthValue: '', additionalEntries: [] });
    setHeadMeasurements({ birthValue: '', additionalEntries: [] });
    setWeightMeasurements({ birthValue: '', additionalEntries: [] });
  }, [gender, fentonHC, fentonLength, fentonWeight, graphType, ageForGraph]);

  const percentileColors = {
    97.7: '#000000',
    97: '#000000',
    90: '#2A2A2A',
    50: '#555555',
    10: '#7F7F7F',
    3: '#A8A8A8',
    2.3: '#A8A8A8',
  } as const;

  const handleSelectGender = (g: 'boy' | 'girl') => {
    setGender(g);
    setShowLengthGraph(true);
    setShowHeadGraph(true);
    setShowWeightGraph(true);
    setLengthMeasurements({ birthValue: '', additionalEntries: [] });
    setHeadMeasurements({ birthValue: '', additionalEntries: [] });
    setWeightMeasurements({ birthValue: '', additionalEntries: [] });
  };

  const outOfRangeStyle: CSSProperties = {
    fontSize: '1.25rem',
    textAlign: 'center',
    padding: '2rem',
  };

  // Out of range guards
  if (typeof fixedAgeInWeeks === 'number' && fixedAgeInWeeks < 22) {
    return (
      <div className="graphs-container-no-border">
        <h2 style={{textAlign: 'center'}}>גרף התפתחות{graphType && ` ${graphType}`}</h2>
        <div style={outOfRangeStyle}>
          היילוד בשבוע הריון {fixedAgeInWeeks}, גרף {graphType} מציגים נתונים מגיל 22 שבועות
        </div>
      </div>
    );
  }

  if (typeof whoMonths === 'number' && whoMonths > 24) {
    return (
      <div className="graphs-container-no-border">
        <h2 style={{textAlign: 'center'}}>גרף התפתחות{graphType && ` ${graphType}`}</h2>
        <div style={outOfRangeStyle}>
          היילוד בן {whoMonths} חודשים, גרף {graphType} מציגים נתונים עד גיל 24 חודשים
        </div>
      </div>
    );
  }

  return (
    <div className="graphs-container-no-border">
      <h2 style={{textAlign: 'center'}}>גרף התפתחות{graphType && ` ${graphType}`}</h2>

      <div>
        {graphType === 'פנטון' ? (
          <p className="result-text">שבוע הריון: {fixedWeeksNum} שבועות</p>
        ) : (
          <p className="result-text">חודש לטובת הגרף: {whoMonths} חודשים</p>
        )}
      </div>

      {/* Gender Selection */}
      <div className={`gender-row ${showGenderError ? 'gender-error-border' : ''}`}>
        <span className="gender-label">מין:</span>
        <div className="gender-options">
          <button type="button" className="gender-option" onClick={() => handleSelectGender('boy')}>
            <img src={gender === 'boy' ? radioFilled : radioEmpty} className="gender-icon" alt="" />
            <span className="gender-text">בן</span>
          </button>
          <button type="button" className="gender-option" onClick={() => handleSelectGender('girl')}>
            <img src={gender === 'girl' ? radioFilled : radioEmpty} className="gender-icon" alt="" />
            <span className="gender-text">בת</span>
          </button>
        </div>
      </div>

      <div
        className="tabs-container"
        style={{
          display: 'flex',
          border: '1px solid #5a7aaf',
          borderRadius: '10px',
          overflow: 'hidden',
          width: '100%',
          marginBottom: '1rem',
        }}
      >
        {/* Length */}
        <button
          onClick={() => setActiveTab('length')}
          style={{
            flex: 1,
            padding: '0.5rem 1rem',
            background: activeTab === 'length' ? '#2f5597' : '#f0f3f8',
            color: activeTab === 'length' ? 'white' : '#2f5597',
            fontWeight: 'bold',
            fontSize: '1.25rem',
            cursor: 'pointer',
            border: 'none',
            borderLeft: '1px solid #8596AF',
          }}
        >
          אורך
        </button>

        {/* Head */}
        <button
          onClick={() => setActiveTab('head')}
          style={{
            flex: 1,
            padding: '0.5rem 1rem',
            background: activeTab === 'head' ? '#2f5597' : '#f0f3f8',
            color: activeTab === 'head' ? 'white' : '#2f5597',
            fontWeight: 'bold',
            fontSize: '1.25rem',
            cursor: 'pointer',
            border: 'none',
            borderLeft: '1px solid #8596AF',
          }}
        >
          היקף ראש
        </button>

        {/* Weight */}
        <button
          onClick={() => setActiveTab('weight')}
          style={{
            flex: 1,
            padding: '0.5rem 1rem',
            background: activeTab === 'weight' ? '#2f5597' : '#f0f3f8',
            color: activeTab === 'weight' ? 'white' : '#2f5597',
            fontWeight: 'bold',
            fontSize: '1.25rem',
            cursor: 'pointer',
            border: 'none',
            borderLeft: '1px solid #8596AF',
          }}
        >
          משקל
        </button>
      </div>

      <div style={{ position: 'relative' }}>
        <div style={{
          display: activeTab === 'length' ? 'block' : 'none',
        }}>
          <MeasurementForm
            config={MEASUREMENTS_CONFIG.length}
            graphType={graphType}
            value={fentonLength}
            setValue={setFentonLength}
            gender={gender}
            age={ageForGraph || 0}
            percentage={percentageLength}
            setPercentage={setPercentageLength}
            showGraph={showLengthGraph}
            setShowGraph={setShowLengthGraph}
            findPercentage={findPercentage}
          />

          <MeasurementGraph
            config={MEASUREMENTS_CONFIG.length}
            graphType={graphType}
            showGraph={showLengthGraph}
            gender={gender}
            value={fentonLength}
            dataSet={dataSetLength}
            age={ageForGraph || 0}
            percentileColors={percentileColors}
            measurementState={lengthMeasurements}
            gestationWeeks={gestationWeeks}
          />

          {showLengthGraph && gender && fentonLength && (
            <AdditionalMeasurements
              config={MEASUREMENTS_CONFIG.length}
              graphType={graphType}
              measurementState={lengthMeasurements}
              setMeasurementState={setLengthMeasurements}
              birthDate={birthDate}
              calculateWeek={calculateGestationWeek}
            />
          )}
        </div>

        <div style={{
          display: activeTab === 'head' ? 'block' : 'none',
        }}>
          <MeasurementForm
            config={MEASUREMENTS_CONFIG.head}
            graphType={graphType}
            value={fentonHC}
            setValue={setFentonHC}
            gender={gender}
            age={ageForGraph || 0}
            percentage={percentageHead}
            setPercentage={setPercentageHead}
            showGraph={showHeadGraph}
            setShowGraph={setShowHeadGraph}
            findPercentage={findPercentage}
          />

          <MeasurementGraph
            config={MEASUREMENTS_CONFIG.head}
            graphType={graphType}
            showGraph={showHeadGraph}
            gender={gender}
            value={fentonHC}
            dataSet={dataSetHead}
            age={ageForGraph || 0}
            percentileColors={percentileColors}
            measurementState={headMeasurements}
            gestationWeeks={gestationWeeks}
          />

          {showHeadGraph && gender && fentonHC && (
            <AdditionalMeasurements
              config={MEASUREMENTS_CONFIG.head}
              graphType={graphType}
              measurementState={headMeasurements}
              setMeasurementState={setHeadMeasurements}
              birthDate={birthDate}
              calculateWeek={calculateGestationWeek}
            />
          )}
        </div>

        <div style={{
          display: activeTab === 'weight' ? 'block' : 'none',
        }}>
          <MeasurementForm
            config={MEASUREMENTS_CONFIG.weight}
            graphType={graphType}
            value={fentonWeight}
            setValue={setFentonWeight}
            gender={gender}
            age={ageForGraph || 0}
            percentage={percentageWeight}
            setPercentage={setPercentageWeight}
            showGraph={showWeightGraph}
            setShowGraph={setShowWeightGraph}
            findPercentage={findPercentage}
          />

          <MeasurementGraph
            config={MEASUREMENTS_CONFIG.weight}
            graphType={graphType}
            showGraph={showWeightGraph}
            gender={gender}
            value={fentonWeight}
            dataSet={dataSetWeight}
            age={ageForGraph || 0}
            percentileColors={percentileColors}
            measurementState={weightMeasurements}
            gestationWeeks={gestationWeeks}
          />

          {showWeightGraph && gender && fentonWeight && (
            <AdditionalMeasurements
              config={MEASUREMENTS_CONFIG.weight}
              graphType={graphType}
              measurementState={weightMeasurements}
              setMeasurementState={setWeightMeasurements}
              birthDate={birthDate}
              calculateWeek={calculateGestationWeek}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default GrowthCharts;